import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FiMail, FiPhone, FiMapPin, FiSend, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { api } from '../utils/api';
import { useRefreshListener, REFRESH_EVENTS } from '../utils/refreshEvents';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [settings, setSettings] = useState(null);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await api.getSettings();
      setSettings(res.data);
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres:', error);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Écouter les événements de rafraîchissement depuis l'admin
  useRefreshListener(REFRESH_EVENTS.SETTINGS, fetchSettings);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      await api.submitContact(formData);
      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'envoi du message. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const contactItems = settings ? [
    {
      icon: FiPhone,
      title: 'Téléphone',
      content: settings.contact.phone,
      link: `tel:${settings.contact.phone.replace(/\s/g, '')}`,
      color: '#4a7c2a'
    },
    {
      icon: FiMail,
      title: 'Email',
      content: settings.contact.email,
      link: `mailto:${settings.contact.email}`,
      color: '#6b8e3f'
    },
    {
      icon: FiMapPin,
      title: 'Localisation',
      content: settings.contact.location,
      link: null,
      color: '#2d5016'
    }
  ] : [];

  return (
    <div className="contact-page">
      <div className="page-header">
        <div className="page-header-background"></div>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1>Contactez-nous</h1>
            <p>Une question ? Un projet ? N'hésitez pas à nous contacter</p>
          </motion.div>
        </div>
      </div>

      <section className="section">
        <div className="container">
          <div className="contact-content">
            <motion.div
              className="contact-info"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2>Informations de contact</h2>
              <p>
                Contact pour prise de rendez-vous ou consulting
              </p>
              
              <div className="contact-details">
                {contactItems.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <motion.div
                      key={index}
                      className="contact-item"
                      whileHover={{ scale: 1.05, x: 10 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      <div className="contact-icon" style={{ background: `linear-gradient(135deg, ${item.color}, ${item.color}dd)` }}>
                        <Icon />
                      </div>
                      <div>
                        <h3>{item.title}</h3>
                        {item.link ? (
                          <a href={item.link}>{item.content}</a>
                        ) : (
                          <p>{item.content}</p>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>

            <motion.div
              className="contact-form-container"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2>Envoyez-nous un message</h2>
              
              {success && (
                <motion.div
                  className="alert alert-success"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <FiCheckCircle className="alert-icon" />
                  Message envoyé avec succès ! Nous vous répondrons dans les plus brefs délais.
                </motion.div>
              )}

              {error && (
                <motion.div
                  className="alert alert-error"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <FiAlertCircle className="alert-icon" />
                  {error}
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Nom complet *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Votre nom"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="votre@email.com"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Téléphone</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="07 12 34 56 78"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="subject">Sujet *</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      placeholder="Objet de votre message"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    placeholder="Votre message..."
                  ></textarea>
                </div>

                <motion.button
                  type="submit"
                  className="btn btn-primary btn-large"
                  disabled={loading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {loading ? 'Envoi en cours...' : (
                    <>
                      Envoyer le message
                      <FiSend className="btn-icon" />
                    </>
                  )}
                </motion.button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
